/**
 * Admin Empty Searches page — "Populære søgninger uden resultat".
 *
 * Surfaces every search the user makes that returned 0 hard matches, so the
 * admin can spot gaps in the recipe catalog (missing recipes, missing
 * aliases, missing ingredients) and grow SlushBook from real user behavior.
 *
 * Two views in one screen:
 *   1. Top 20 — aggregated by normalized query, sorted by count.
 *   2. Recent — raw rows, latest first (for debugging / spot-checking).
 *
 * This is an analysis-only tool. It does NOT change user-facing search,
 * sorting, or presentation. Backend endpoints are admin-guarded.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';

const ROLE_BADGE = {
  guest: 'bg-gray-100 text-gray-700',
  pro: 'bg-amber-100 text-amber-800',
  admin: 'bg-red-100 text-red-800',
  editor: 'bg-blue-100 text-blue-800',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('da-DK', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const AdminEmptySearchesPage = () => {
  const [topRows, setTopRows] = useState([]);
  const [recentRows, setRecentRows] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('top'); // 'top' | 'recent'

  const fetchTop = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/empty-searches/top`, {
        params: { days, limit: 20 },
      });
      setTopRows(r.data.rows || []);
    } catch (err) {
      toast.error('Kunne ikke hente top-søgninger');
    }
  }, [days]);

  const fetchRecent = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/empty-searches/recent`, {
        params: { limit: 100 },
      });
      setRecentRows(r.data.rows || []);
    } catch (err) {
      toast.error('Kunne ikke hente seneste søgninger');
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchTop(), fetchRecent()]).finally(() => setLoading(false));
  }, [fetchTop, fetchRecent]);

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl" data-testid="admin-empty-searches-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Populære søgninger uden resultat
        </h1>
        <p className="text-gray-600 max-w-3xl">
          Brugernes søgninger der ikke matchede en eksisterende opskrift. Brug
          listen til at finde manglende opskrifter, aliaser og ingredienser —
          så SlushBook kan vokse efter reel brugeradfærd.
        </p>
      </div>

      {/* Tabs + period filter */}
      <div className="flex flex-wrap items-center gap-4 mb-6 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setActiveTab('top')}
          data-testid="tab-top-searches"
          className={`pb-3 px-1 -mb-px text-sm font-semibold transition-colors ${
            activeTab === 'top'
              ? 'border-b-2 border-purple-600 text-purple-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Top 20 ({topRows.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('recent')}
          data-testid="tab-recent-searches"
          className={`pb-3 px-1 -mb-px text-sm font-semibold transition-colors ${
            activeTab === 'recent'
              ? 'border-b-2 border-purple-600 text-purple-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Seneste rå søgninger ({recentRows.length})
        </button>
        {activeTab === 'top' && (
          <div className="ml-auto flex items-center gap-2 text-sm pb-3">
            <label htmlFor="days-select" className="text-gray-600">Periode:</label>
            <select
              id="days-select"
              data-testid="days-filter"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-2 py-1 bg-white"
            >
              <option value={7}>Sidste 7 dage</option>
              <option value={30}>Sidste 30 dage</option>
              <option value={90}>Sidste 90 dage</option>
              <option value={365}>Sidste år</option>
            </select>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Indlæser…</div>
      ) : activeTab === 'top' ? (
        <TopTable rows={topRows} />
      ) : (
        <RecentTable rows={recentRows} />
      )}
    </div>
  );
};

const TopTable = ({ rows }) => {
  if (!rows.length) {
    return (
      <div className="bg-gray-50 rounded-xl p-8 text-center text-gray-500" data-testid="empty-state-top">
        Ingen tomme søgninger i den valgte periode 🎉
      </div>
    );
  }
  return (
    <div className="overflow-x-auto border border-gray-200 rounded-xl bg-white">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-600 text-left">
          <tr>
            <th className="px-4 py-3 font-semibold">#</th>
            <th className="px-4 py-3 font-semibold">Søgeord</th>
            <th className="px-4 py-3 font-semibold text-center">Antal gange</th>
            <th className="px-4 py-3 font-semibold text-center">Klik på AI</th>
            <th className="px-4 py-3 font-semibold text-center">M. forslag</th>
            <th className="px-4 py-3 font-semibold">Roller</th>
            <th className="px-4 py-3 font-semibold">Seneste søgning</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.query_normalized}
              data-testid={`top-search-row-${i}`}
              className="border-t border-gray-100 hover:bg-purple-50/40"
            >
              <td className="px-4 py-3 text-gray-400">{i + 1}</td>
              <td className="px-4 py-3 font-semibold text-gray-900">
                <div>{r.latest_query}</div>
                {r.latest_query !== r.query_normalized && (
                  <div className="text-xs text-gray-400 font-normal">
                    normaliseret: {r.query_normalized}
                  </div>
                )}
              </td>
              <td className="px-4 py-3 text-center">
                <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 font-bold">
                  {r.count}
                </span>
              </td>
              <td className="px-4 py-3 text-center text-gray-700">{r.ai_clicks}</td>
              <td className="px-4 py-3 text-center text-gray-700">{r.had_suggestions_count}</td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {(r.roles || []).map((role) => (
                    <span
                      key={role}
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        ROLE_BADGE[role] || 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </td>
              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                {formatDate(r.last_seen)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RecentTable = ({ rows }) => {
  if (!rows.length) {
    return (
      <div className="bg-gray-50 rounded-xl p-8 text-center text-gray-500" data-testid="empty-state-recent">
        Ingen tomme søgninger fundet endnu.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto border border-gray-200 rounded-xl bg-white">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-600 text-left">
          <tr>
            <th className="px-4 py-3 font-semibold">Tid</th>
            <th className="px-4 py-3 font-semibold">Søgning</th>
            <th className="px-4 py-3 font-semibold">Sprog</th>
            <th className="px-4 py-3 font-semibold">Rolle</th>
            <th className="px-4 py-3 font-semibold text-center">AI vist</th>
            <th className="px-4 py-3 font-semibold text-center">AI klikket</th>
            <th className="px-4 py-3 font-semibold text-center">Forslag</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={`recent-search-row-${r.id}`}
              className="border-t border-gray-100 hover:bg-purple-50/40"
            >
              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                {formatDate(r.created_at)}
              </td>
              <td className="px-4 py-3 font-semibold text-gray-900">
                <div>{r.query}</div>
                {r.query !== r.query_normalized && (
                  <div className="text-xs text-gray-400 font-normal">
                    normaliseret: {r.query_normalized}
                  </div>
                )}
              </td>
              <td className="px-4 py-3 text-gray-600 uppercase text-xs">{r.lang}</td>
              <td className="px-4 py-3">
                <span
                  className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    ROLE_BADGE[r.user_role] || 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {r.user_role}
                </span>
              </td>
              <td className="px-4 py-3 text-center">{r.ai_button_shown ? '✓' : '—'}</td>
              <td className="px-4 py-3 text-center">{r.ai_clicked ? '✅' : '—'}</td>
              <td className="px-4 py-3 text-center">{r.had_suggestions ? '✓' : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdminEmptySearchesPage;
